import { useCallback, useMemo, useRef, useState } from 'react';

import { useLazyFindClientsByPhoneQuery } from '@app/api/Clients';
import type { FindClientsByPhoneResponse } from '@app/api/Clients';
import { FRONT_WARNING, WARNING } from '@app/constants';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

// Итог поиска по номеру. stale — пришёл ответ на прежний номер, его
// не применяем (task/11629).
export type PhoneClientSearchResult = 'found' | 'not-found' | 'error' | 'stale';

// idle — поиска нет или клиент для номера выбран.
type PhoneClientSearchStatus =
  | 'idle'
  | 'loading'
  | 'found'
  | 'not-found'
  | 'error';

export const PHONE_CLIENT_FOUND_ERROR =
  'По этому номеру есть клиенты. Нажмите на поле и выберите клиента или измените номер.';
export const PHONE_CLIENT_LOADING_ERROR =
  'Поиск клиента по номеру ещё идёт. Подождите и нажмите «Сохранить» снова.';
export const PHONE_CLIENT_FAILED_ERROR =
  'Не удалось проверить номер. Измените номер или выберите клиента в поле «Поиск».';
export const PHONE_CLIENT_NO_BRANCH_ERROR =
  'Не удалось найти клиента: филиал записи не определён. Обратитесь в техподдержку.';

// Пока клиент для номера не определён, запись не сохраняется: текст
// ошибки у поля телефона зависит от того, почему он не определён.
const PHONE_CLIENT_SEARCH_ERRORS: Record<
  PhoneClientSearchStatus,
  string | null
> = {
  idle: null,
  'not-found': null,
  loading: PHONE_CLIENT_LOADING_ERROR,
  found: PHONE_CLIENT_FOUND_ERROR,
  error: PHONE_CLIENT_FAILED_ERROR,
};

// Текст ошибки поля телефона после ответа поиска: им заменяется текст
// «Поиск клиента по номеру ещё идёт», если «Сохранить» нажали до ответа.
export const PHONE_CLIENT_RESULT_ERRORS: Record<
  Exclude<PhoneClientSearchResult, 'stale'>,
  string | null
> = {
  found: PHONE_CLIENT_FOUND_ERROR,
  'not-found': null,
  error: PHONE_CLIENT_FAILED_ERROR,
};

interface UseRecordPhoneClientSearchProps {
  recordActiveBranchId: number | null | undefined;
}

interface SearchPhoneClientsArgs {
  phone: string;
  branchId: number | null | undefined;
}

// Поиск клиента по номеру из поля «Мобильный телефон» (task/11629).
// Меню под полем открыто, пока поле в фокусе: загрузка, найденные клиенты
// или «Клиенты не найдены», как у поля «Поиск».
export const useRecordPhoneClientSearch = ({
  recordActiveBranchId,
}: UseRecordPhoneClientSearchProps) => {
  const { showNotification } = useNotification();
  const [findClientsByPhone] = useLazyFindClientsByPhoneQuery();
  const [phoneClients, setPhoneClients] = useState<FindClientsByPhoneResponse>(
    []
  );
  const [phoneClientSearchStatus, setPhoneClientSearchStatus] =
    useState<PhoneClientSearchStatus>('idle');
  const [isPhoneClientMenuOpen, setIsPhoneClientMenuOpen] = useState(false);
  // Номер последнего поиска: меню и ошибка поиска относятся только к нему.
  const [searchedPhone, setSearchedPhone] = useState('');
  const phoneSearchRequestIdRef = useRef(0);
  const isPhoneInputFocusedRef = useRef(false);
  const isCountryListOpenRef = useRef(false);

  // Меню клиентов открывается, только когда фокус в поле телефона и список
  // стран закрыт: иначе меню легло бы поверх списка стран.
  const canOpenPhoneClientMenu = useCallback(
    () => isPhoneInputFocusedRef.current && !isCountryListOpenRef.current,
    []
  );

  const resetPhoneClientSearch = useCallback(() => {
    phoneSearchRequestIdRef.current += 1;
    setPhoneClients([]);
    setSearchedPhone('');
    setPhoneClientSearchStatus('idle');
    setIsPhoneClientMenuOpen(false);
  }, []);

  const searchPhoneClients = useCallback(
    async ({
      phone,
      branchId,
    }: SearchPhoneClientsArgs): Promise<PhoneClientSearchResult> => {
      const requestId = ++phoneSearchRequestIdRef.current;

      setPhoneClients([]);
      setSearchedPhone(phone);

      if (!branchId || !recordActiveBranchId) {
        setPhoneClientSearchStatus('error');
        setIsPhoneClientMenuOpen(false);
        showNotification(PHONE_CLIENT_NO_BRANCH_ERROR, FRONT_WARNING);

        return 'error';
      }

      setPhoneClientSearchStatus('loading');
      setIsPhoneClientMenuOpen(canOpenPhoneClientMenu());

      try {
        const response = await findClientsByPhone({
          branchId,
          phone1: phone,
          phone2: phone,
          ActiveBranchId: recordActiveBranchId,
        }).unwrap();

        if (requestId !== phoneSearchRequestIdRef.current) return 'stale';

        const clients = response ?? [];
        const isFound = clients.length > 0;

        setPhoneClients(clients);
        setPhoneClientSearchStatus(isFound ? 'found' : 'not-found');
        setIsPhoneClientMenuOpen(canOpenPhoneClientMenu());

        return isFound ? 'found' : 'not-found';
      } catch (error) {
        if (requestId !== phoneSearchRequestIdRef.current) return 'stale';

        setPhoneClientSearchStatus('error');
        setIsPhoneClientMenuOpen(false);
        showNotification(error, WARNING);

        return 'error';
      }
    },
    [
      findClientsByPhone,
      recordActiveBranchId,
      showNotification,
      canOpenPhoneClientMenu,
    ]
  );

  // Фокус в поле открывает меню снова, только если клиенты найдены и не
  // выбраны. Уход из поля (Tab, клик мимо, клик по флагу) меню закрывает.
  const onPhoneInputFocus = useCallback(() => {
    isPhoneInputFocusedRef.current = true;

    if (phoneClientSearchStatus === 'found' && canOpenPhoneClientMenu()) {
      setIsPhoneClientMenuOpen(true);
    }
  }, [phoneClientSearchStatus, canOpenPhoneClientMenu]);

  const onPhoneInputBlur = useCallback(() => {
    isPhoneInputFocusedRef.current = false;
    setIsPhoneClientMenuOpen(false);
  }, []);

  // Клик по флагу или наведение на него открывает список стран, клик
  // возвращает фокус в поле (BaseInput из rc-input): меню клиентов при этом
  // закрыто. Список закрылся, фокус в поле, клиенты не выбраны — меню
  // открывается снова.
  const onPhoneCountryListOpenChange = useCallback(
    (open: boolean) => {
      isCountryListOpenRef.current = open;

      if (open) {
        setIsPhoneClientMenuOpen(false);
      } else if (
        phoneClientSearchStatus === 'found' &&
        canOpenPhoneClientMenu()
      ) {
        setIsPhoneClientMenuOpen(true);
      }
    },
    [phoneClientSearchStatus, canOpenPhoneClientMenu]
  );

  const phoneClientSearchError = useMemo(
    () => PHONE_CLIENT_SEARCH_ERRORS[phoneClientSearchStatus],
    [phoneClientSearchStatus]
  );

  return {
    phoneClients,
    isPhoneClientMenuOpen,
    isPhoneClientSearchLoading: phoneClientSearchStatus === 'loading',
    isPhoneClientSearchSuccess:
      phoneClientSearchStatus === 'found' ||
      phoneClientSearchStatus === 'not-found',
    phoneClientSearchError,
    searchedPhone,
    searchPhoneClients,
    resetPhoneClientSearch,
    onPhoneInputFocus,
    onPhoneInputBlur,
    onPhoneCountryListOpenChange,
  };
};
